import { Remove } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from "@mui/material";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomButton from "../elements/CustomButton";
import InputField from "../elements/InputField";
import FormattedTextArea from "../elements/FormattedTextArea";
import Dropdown2 from "../elements/Dropdown2";

const tagOptions = [
  { value: "None", label: "None" },
  { value: "nice-to-have", label: "Nice to have" },
  { value: "essential", label: "Essential" },
  { value: "extra", label: "Extra" },
];

const CategoryForm = ({ onSubmit, category }) => {
  return (
    <Formik
      initialValues={{
        name: category?.name || "",
        description: category?.description || "",
        insights: category?.insights || "",
        image: category?.icon || null,
        showMdBadge: category?.show_md_badge || false,
        hide: category?.hide || false,
        tag: category?.tag === "" ? "None" : category?.tag || "None",
        categoryDetails:
          category?.category_details.length > 0
            ? category?.category_details
            : [""],
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().max(255).required("Name is required"),
        description: Yup.string().max(700),
        insights: Yup.string().max(400),
        image: Yup.mixed().nullable(),
        categoryDetails: Yup.array(),
      })}
      onSubmit={(values) => {
        const transformedValues = {
          ...values,
          tag: values.tag === "None" ? "" : values.tag,
        };
        onSubmit(transformedValues);
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        touched,
        values,
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <InputField
                  label="Name"
                  id="name"
                  type="text"
                  value={values.name}
                  name="name"
                  fullWidth
                  placeholder="Category name"
                  error={Boolean(touched.name && errors.name)}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <InputLabel>Category icon</InputLabel>
                <Grid
                  item
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: values.image ? "space-evenly" : "center",
                  }}
                >
                  <div style={{ marginRight: 10 }}>
                    {values.image && typeof values.image === "string" ? (
                      <img
                        alt="Selected File"
                        src={values.image}
                        style={{ width: 50, height: 50 }}
                      />
                    ) : values.image && typeof values.image !== "string" ? (
                      <img
                        alt="Selected File"
                        src={URL.createObjectURL(values.image)}
                        style={{ width: 50, height: 50 }}
                      />
                    ) : null}
                  </div>
                  <div>
                    <input
                      accept="image/png, image/jpeg, image/jpg"
                      id="image"
                      name="image"
                      type="file"
                      onChange={(event) => {
                        const selectedFile = event.currentTarget.files[0];
                        setFieldValue("image", selectedFile);
                      }}
                      style={{ display: "none" }}
                    />
                    <label htmlFor="image">
                      <Button variant="outlined" size="small" component="span">
                        {values.image ? "Change Icon" : "Upload Icon"}
                      </Button>
                    </label>
                  </div>
                </Grid>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <FormattedTextArea
                  label="Description"
                  id="description"
                  name="description"
                  rows={5}
                  value={values.description}
                  placeholder="Enter description"
                  error={Boolean(touched.description && errors.description)}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Dropdown2
                  elementName="tag"
                  selected={values.tag}
                  handleChange={(e) => {
                    handleChange(e);
                  }}
                  elementLabel="Tag"
                  options={tagOptions}
                  error={Boolean(touched.tag && errors.tag)}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <FormattedTextArea
                label={
                  <span>
                    M&D insights
                    {/* <span
                      style={{
                        display: 'block',
                        fontSize: 'small',
                        color: 'gray',
                      }}>
                      To display a word in <strong>bold</strong>, wrap it in
                      asterisks (*) and for <em>italics</em>, wrap it in
                      (&lt;i&gt;&lt;/i&gt;)
                    </span> */}
                  </span>
                }
                id="insights"
                name="insights"
                rows={3}
                value={values.insights}
                placeholder="Enter M&D insights"
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.insights && errors.insights)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.hide}
                    onChange={handleChange}
                    name="hide"
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Hide category</Typography>}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel>Category details</InputLabel>
                <FormHelperText sx={{ fontStyle: "italic", color: "gray" }}>
                  To display field names with capital letters, use underscores.
                  For example, if you want the UI to show 'FYI', enter it as
                  'f_y_i'.
                </FormHelperText>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.showMdBadge}
                      onChange={handleChange}
                      name="showMdBadge"
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">Show M&D Awards</Typography>
                  }
                />
                <FieldArray
                  name="categoryDetails"
                  render={(arrayHelpers) => (
                    <div>
                      {values.categoryDetails.map((point, index) => (
                        <div
                          key={index}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <InputField
                            id={`categoryDetails[${index}]`}
                            name={`categoryDetails[${index}]`}
                            type="text"
                            value={point}
                            multiline
                            onChange={handleChange}
                            fullWidth
                          />
                          <IconButton
                            onClick={() => arrayHelpers.remove(index)}
                            color="secondary"
                            disabled={index === 0}
                          >
                            <Remove />
                          </IconButton>
                        </div>
                      ))}
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={2}
                      >
                        <Button
                          onClick={() => arrayHelpers.push("")}
                          color="primary"
                          variant="text"
                          sx={{ mt: "10px" }}
                        >
                          Add field
                        </Button>
                      </Stack>
                    </div>
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              {errors.submit && (
                <FormHelperText error>{errors.submit}</FormHelperText>
              )}
              <CustomButton />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default CategoryForm;
